import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Avatar, Badge } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { hubForPath, visibleHubs, defaultPath, NavPerms, FALLBACK_MODULES } from '../../config/navHubs';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/config';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  Home,
  School,
  Analytics,
  EmojiEvents,
  Forum,
  Assignment,
  AdminPanelSettings,
  Spa,
  Logout,
  BeachAccess,
  Assessment,
  DarkModeOutlined,
  LightModeOutlined,
} from '@mui/icons-material';

const RAIL_WIDTH = 80;

const RAIL_STYLES = {
  navSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    paddingBottom: '8px',
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.2)', borderRadius: '2px' },
    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.3)' },
  },
  navItemsGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
  },
};

interface NotificationCounts {
  announcements: number;
  learning: number;
  games: number;
  store: number;
  chat: number;
  tasks: number;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
  managerOnly?: boolean;
  moduleName?: string;
}

// Hub id → rail icon.
const HUB_ICONS: Record<string, React.ReactNode> = {
  home: <Home />, grow: <School />, wellbeing: <Spa />, performance: <Assessment />,
  rewards: <EmojiEvents />, workspace: <Assignment />, connect: <Forum />, leave: <BeachAccess />,
};
// Which legacy per-item badge counts roll up onto which hub.
const HUB_BADGE_IDS: Record<string, string[]> = {
  dashboard: ['dashboard'], grow: ['learning', 'games'], connect: ['announcements', 'chat'],
  rewards: ['store'], workspace: ['tasks'],
};

// Single Admin Hub link instead of extended menu
const adminItems: NavItem[] = [
  { id: 'insights', label: 'Insights', icon: <Analytics />, path: '/insights', adminOnly: true },
  { id: 'admin-hub', label: 'Admin Hub', icon: <AdminPanelSettings />, path: '/admin', adminOnly: true },
];

function NavigationRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branding, mode, toggleMode } = useBranding();
  const [enabledModules, setEnabledModules] = useState<string[]>(FALLBACK_MODULES);
  const [cultureScheduledToday, setCultureScheduledToday] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState<NotificationCounts>({
    announcements: 0,
    learning: 0,
    games: 0,
    store: 0,
    chat: 0,
    tasks: 0,
  });

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await apiClient.get('/company/modules');
        if (response.data?.success && response.data?.data) {
          const activeModules = response.data.data
            .filter((m: { isActive: boolean }) => m.isActive)
            .map((m: { moduleName: string }) => m.moduleName);
          if (activeModules.length) setEnabledModules(activeModules);
        }
      } catch (error) {
        // Keep FALLBACK_MODULES — never blank the nav on a failed fetch.
      }
    };
    fetchModules();
    apiClient.get('/culture/is-scheduled-today').then(res => {
      if (res.data?.success && res.data?.data?.isScheduled) setCultureScheduledToday(true);
    }).catch(() => {});
  }, []);

  // Fetch notification counts for badges
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      try {
        const response = await apiClient.get('/notifications/counts');
        if (response.data?.success && response.data?.data) {
          setNotificationCounts(response.data.data);
        }
      } catch (error) {
        // Silently fail - badges will show 0
      }
    };

    fetchNotificationCounts();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchNotificationCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const isAdmin = user && ['admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isManager = user && ['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';

  // Check if a module is enabled

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get badge count for a nav item
  const getBadgeCount = (itemId: string): number => {
    switch (itemId) {
      case 'announcements':
        return notificationCounts.announcements;
      case 'policies':
        return (notificationCounts as any).policies || 0;
      case 'learning':
      case 'microlearning':
        return notificationCounts.learning;
      case 'games':
        return notificationCounts.games;
      case 'store':
        return notificationCounts.store;
      case 'chat':
      case 'pulse':
        return notificationCounts.chat;
      case 'tasks':
        return notificationCounts.tasks;
      default:
        return 0;
    }
  };

  const sidebarBg = branding.sidebarColor || '#1A1D1F';
  const sidebarText = branding.sidebarTextColor || '#FFFFFF';
  const primaryColor = branding.primaryColor || '#2D5BE3';

  return (
    <Box sx={{
      width: RAIL_WIDTH,
      height: '100vh',
      background: sidebarBg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '16px 0',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1200,
    }}>
      <Tooltip title={branding.companyName || 'MoreMe AI'} placement="right">
        <Box
          onClick={() => navigate(isSuperSuperAdmin ? '/admin/super' : '/dashboard')}
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: branding.logoUrl ? 'transparent' : primaryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          {branding.logoUrl ? (
            <Box
              component="img"
              src={resolveAssetUrl(branding.logoUrl)}
              alt={branding.companyName || 'Logo'}
              sx={{ width: 40, height: 40, objectFit: 'contain' }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/brand/logo-mark.svg`}
              alt="MoreMe AI"
              sx={{ width: 28, height: 28, filter: 'brightness(10)' }}
            />
          )}
        </Box>
      </Tooltip>

      <Box sx={RAIL_STYLES.navSection}>
        {!isSuperSuperAdmin && (
        <Box sx={RAIL_STYLES.navItemsGroup}>
            {(() => {
              const navPerms: NavPerms = { enabledModules, isManager: !!isManager, isAdmin: !!isAdmin };
              const activeHub = hubForPath(location.pathname);
              return visibleHubs(navPerms).map((hub) => {
                const target = defaultPath(hub, navPerms);
                const active = activeHub?.id === hub.id;
                const badgeCount = (HUB_BADGE_IDS[hub.id] || []).reduce((s, id) => s + getBadgeCount(id), 0);
                const cultureCue = hub.id === 'connect' && cultureScheduledToday;
                return (
                  <Tooltip key={hub.id} title={hub.label} placement="right">
                    <span>
                      <IconButton
                        onClick={() => target && navigate(target)}
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          color: active ? primaryColor : `${sidebarText}99`,
                          backgroundColor: active ? `${primaryColor}20` : 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: active ? `${primaryColor}30` : `${sidebarText}15`,
                            color: active ? primaryColor : sidebarText,
                          },
                          '& svg': { fontSize: 24 },
                        }}
                      >
                        {(badgeCount > 0 || cultureCue) ? (
                          <Badge
                            badgeContent={cultureCue && badgeCount === 0 ? '!' : (badgeCount > 99 ? '99+' : badgeCount)}
                            max={99}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: cultureCue ? (branding.secondaryColor || '#8B5CF6') : '#EF4444',
                                color: 'white', fontSize: '10px', fontWeight: 700,
                                minWidth: '18px', height: '18px', borderRadius: '9px',
                                padding: '0 4px', border: `2px solid ${sidebarBg}`,
                              },
                            }}
                          >
                            {HUB_ICONS[hub.icon]}
                          </Badge>
                        ) : HUB_ICONS[hub.icon]}
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              });
            })()}
        </Box>
        )}

        {isAdmin && (
          <Box sx={{ ...RAIL_STYLES.navItemsGroup, pt: 2, borderTop: `1px solid ${sidebarText}1A`, mt: 2 }}>
            {adminItems.map((item) => {
              const superAdminHub = item.id === 'admin-hub' && isSuperSuperAdmin;
              return (
              <Tooltip key={item.id} title={superAdminHub ? 'Super Admin Dashboard' : item.label} placement="right">
                <IconButton
                  onClick={() => navigate(superAdminHub ? '/admin/super' : item.path)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    color: isActive(item.path) ? primaryColor : `${sidebarText}99`,
                    backgroundColor: isActive(item.path) ? `${primaryColor}20` : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isActive(item.path) ? `${primaryColor}30` : `${sidebarText}15`,
                      color: isActive(item.path) ? primaryColor : sidebarText,
                    },
                    '& svg': { fontSize: 24 },
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        paddingTop: '16px',
        borderTop: `1px solid ${sidebarText}1A`,
        marginTop: 'auto',
      }}>
        <Tooltip title={mode === 'dark' ? 'Day register' : 'Night register'} placement="right">
          <IconButton
            onClick={toggleMode}
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              color: `${sidebarText}99`,
              '&:hover': { backgroundColor: `${sidebarText}15`, color: sidebarText },
              '& svg': { fontSize: 22 },
            }}
          >
            {mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
          </IconButton>
        </Tooltip>
        {isSuperSuperAdmin ? (
          <Tooltip title="Logout" placement="right">
            <IconButton
              onClick={logout}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                color: `${sidebarText}99`,
                '&:hover': { backgroundColor: `${sidebarText}15`, color: 'var(--color-error)' },
                '& svg': { fontSize: 24 },
              }}
            >
              <Logout />
            </IconButton>
          </Tooltip>
        ) : (
        <Tooltip title={`${user?.firstName} ${user?.lastName}`} placement="right">
          <Avatar
            src={user?.avatar}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              border: `2px solid ${sidebarText}40`,
              background: primaryColor,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: sidebarBg,
            }}
            onClick={() => navigate('/profile')}
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
        </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default NavigationRail;
